import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiInstagram, FiFacebook, FiYoutube, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { userApi, BASE_URL } from '../../api/userApi';
import { toast } from 'react-hot-toast';

const Footer = () => {
  const [logo, setLogo] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await userApi.getLogo();
        if (response?.image) {
          let logoUrl = response.image;
          // If URL is relative (starts with /), prepend API domain
          if (logoUrl && logoUrl.startsWith('/') && !logoUrl.startsWith('http')) {
            const apiDomain = BASE_URL.replace('/api', '');
            logoUrl = `${apiDomain}${logoUrl}`;
          }
          // If URL doesn't start with http/https/data, it might be a relative path
          else if (logoUrl && !logoUrl.startsWith('http') && !logoUrl.startsWith('data:') && !logoUrl.startsWith('blob:')) {
            const apiDomain = BASE_URL.replace('/api', '');
            logoUrl = logoUrl.startsWith('/') ? `${apiDomain}${logoUrl}` : `${apiDomain}/${logoUrl}`;
          }
          setLogo(logoUrl);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogo();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await userApi.subscribeNewsletter(email);
      toast.success('Successfully subscribed to our newsletter!');
      setEmail('');
    } catch (error) {
      toast.error(error.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#FFF8F0]">
      {/* Newsletter Section */}
      <div className="bg-[#faf1e6] border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                Join Our Newsletter
              </h3>
              <p className="text-gray-600">
                Stay updated with our latest collections and exclusive offers
              </p>
            </div>
            <div className="w-full md:w-auto">
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="px-4 py-3 bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full md:w-80"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  className="bg-indigo-900 text-white px-6 py-3 hover:bg-indigo-800 transition-colors flex items-center gap-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="space-y-8">
            <div className="space-y-4">
              <Link to="/" className="inline-block group">
                <div className="flex flex-col items-center">
                  <img 
                    src={logo || `${window.location.origin}/Panchali_logo.png`}
                    alt="Panchali Design Hub" 
                    className="h-20 w-auto transition-transform duration-300 group-hover:scale-105 object-contain"
                    style={{ backgroundColor: 'transparent' }}
                    onError={(e) => {
                      // Fallback if image fails to load
                      e.target.src = `${window.location.origin}/Panchali_logo.png`;
                    }}
                  />
                </div>
              </Link>
              <p className="text-gray-600 leading-relaxed max-w-sm">
                Crafting timeless elegance through contemporary design and traditional artistry.
              </p>
            </div>
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 text-lg">Get in Touch</h4>
              <div className="space-y-3">
                <a href="https://www.google.com/maps/place/Panchali+design+hub/@9.9753053,76.2807695,21z/data=!4m6!3m5!1s0x3b080d2f14f872c7:0xffb67bb27bbf53cd!8m2!3d9.9753423!4d76.2808906!16s%2Fg%2F11vycqs414?entry=ttu&g_ep=EgoyMDI1MDQwMi4xIKXMDSoASAFQAw%3D%3D" 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="flex items-center gap-3 text-gray-600 hover:text-indigo-900 transition-colors group">
                  <FiMapPin className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>Panchali Design Hub, <br /> Market Rd, near St. Teresa's College, <br /> Ernakulam, Kerala 682011</span>
                </a>
                <a href="tel:+911234567890" 
                   className="flex items-center gap-3 text-gray-600 hover:text-indigo-900 transition-colors group">
                  <FiPhone className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>+91 62382 49819</span>
                </a>
                <a href="mailto:NaN" 
                   className="flex items-center gap-3 text-gray-600 hover:text-indigo-900 transition-colors group">
                  <FiMail className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span>care@panchalidesignhub.com</span>
                </a>
              </div>
            </div>
          </div>

          {/* Quick Connect */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6 relative inline-block">
                Quick Connect
                <div className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-gradient-to-r from-indigo-900 to-transparent"></div>
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Customer Service</h4>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/contact" className="text-gray-600 hover:text-indigo-900 transition-colors flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-indigo-900/60"></span>
                        Contact Us
                      </Link>
                    </li>
                    <li>
                      <Link to="/faqs" className="text-gray-600 hover:text-indigo-900 transition-colors flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-indigo-900/60"></span>
                        FAQs
                      </Link>
                    </li>
                    <li>
                      <Link to="/size-guide" className="text-gray-600 hover:text-indigo-900 transition-colors flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-indigo-900/60"></span>
                        Size Guide
                      </Link>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Career</h4>
                  <ul className="space-y-3">
                    <li>
                      <Link to="/careers" className="text-gray-600 hover:text-indigo-900 transition-colors flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-indigo-900/60"></span>
                        Join Our Team
                      </Link>
                    </li>
                    <li>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* About Us Section */}
          <div className="space-y-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 relative inline-block">
              About Us
              <div className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-gradient-to-r from-indigo-900 to-transparent"></div>
            </h3>
            <div className="space-y-4">
              <Link 
                to="/about" 
                className="text-gray-600 hover:text-indigo-900 transition-colors flex items-center gap-2"
              >
                <span className="h-1 w-1 rounded-full bg-indigo-900/60"></span>
                Our Story
              </Link>
              <p className="text-gray-600 leading-relaxed">
                Discover the essence of traditional craftsmanship merged with contemporary design at Panchali Design Hub.
              </p>
            </div>
          </div>

          {/* Policies */}
          <div className="space-y-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 relative inline-block">
              Policies
              <div className="absolute -bottom-2 left-0 w-1/2 h-0.5 bg-gradient-to-r from-indigo-900 to-transparent"></div>
            </h3>
            <ul className="space-y-3">
              {[
                { to: "/privacy-policy#privacy", text: "Privacy Policy" },
                { to: "/privacy-policy#cookie", text: "Ad and Cookie Policy" },
                { to: "/privacy-policy#terms", text: "Terms and Conditions" },
                { to: "/privacy-policy#shipping", text: "Shipping and Returns" }
              ].map((item, index) => (
                <li key={index}>
                  <Link to={item.to} 
                        className="text-gray-600 hover:text-indigo-900 transition-colors flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-indigo-900/60"></span>
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-100 bg-white/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Panchali Design Hub. All rights reserved.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-8">
              {[
                { icon: FiInstagram, href: "https://www.instagram.com/panchalidesigns/", label: "Instagram" },
                { icon: FiFacebook, href: "https://www.facebook.com/people/Panchalidesigns-Hub/61552684619205/", label: "Facebook" },
                { icon: FiYoutube, href: "https://www.youtube.com/@panchalidesignshub/addons", label: "YouTube" }
              ].map((social, index) => (
                <a 
                  key={index}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-indigo-900 transition-colors transform hover:scale-110"
                >
                  <social.icon className="h-6 w-6" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
















